/**
 * ContractorRegister — public contractor signup for /v4/*.
 * Route: /v4/register (BrowserRouter basename /v4)
 */

import { useState, FormEvent, type CSSProperties } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { contractorRegister, storeContractorSession } from '../../api/auth'
import { useAuth } from '../../context/AuthContext'
import PasswordInput from '../../components/auth/PasswordInput'
import { executeTurnstile, turnstileEnabledOnClient } from '../../lib/turnstile'

const shell: CSSProperties = {
  minHeight: '100vh',
  background: 'var(--bg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px 16px',
}

const card: CSSProperties = {
  width: '100%',
  maxWidth: 380,
  background: '#fff',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r)',
  padding: '28px 24px',
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink3)',
  marginBottom: 6,
}

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 14,
}

function namesMatch(person: string, company: string): boolean {
  const a = person.trim().toLowerCase()
  const b = company.trim().toLowerCase()
  return a.length > 0 && a === b
}

export default function ContractorRegister() {
  const navigate = useNavigate()
  const { refetch } = useAuth()
  const [name, setName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const companyLooksLikePerson = namesMatch(name, companyName)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (!companyName.trim()) {
      setError('Company name is required')
      return
    }

    setSubmitting(true)
    try {
      let turnstileToken: string | null = null
      if (turnstileEnabledOnClient()) {
        turnstileToken = await executeTurnstile('signup')
      }
      const result = await contractorRegister(
        name.trim(),
        email.trim(),
        password,
        companyName.trim(),
        true,
        turnstileToken,
      )
      storeContractorSession(result.token)
      await refetch()
      navigate(result.redirect_to || '/pricing', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={shell}>
      <div style={card}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 6px' }}>
          Create your account
        </h1>
        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 22px', lineHeight: 1.5 }}>
          Start your 90-day free trial — no credit card required.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={labelStyle}>Your name</span>
            <input type="text" autoComplete="name" required value={name}
              onChange={e => setName(e.target.value)} className="fld-i" style={inputStyle}
              data-testid="register-your-name" />
          </label>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={labelStyle}>Company name</span>
            <input type="text" autoComplete="organization" required value={companyName}
              onChange={e => setCompanyName(e.target.value)} className="fld-i" style={inputStyle}
              data-testid="register-company-name" />
            <span style={{ display: 'block', fontSize: 11, color: 'var(--ink4)', marginTop: 6, lineHeight: 1.45 }}>
              The business name on your pay apps, e.g. Gravity Construction
            </span>
            {companyLooksLikePerson && (
              <span style={{ display: 'block', fontSize: 11, color: 'var(--amber, #b45309)', marginTop: 6 }}
                role="status" data-testid="register-company-name-warning">
                Company name matches your personal name — pay apps usually show the business name.
              </span>
            )}
          </label>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={labelStyle}>Email</span>
            <input type="email" autoComplete="email" required value={email}
              onChange={e => setEmail(e.target.value)} className="fld-i" style={inputStyle}
              data-testid="register-email" />
          </label>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={labelStyle}>Password</span>
            <PasswordInput autoComplete="new-password" required minLength={8} value={password}
              onChange={e => setPassword(e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={labelStyle}>Confirm Password</span>
            <PasswordInput autoComplete="new-password" required value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} style={inputStyle} />
          </label>

          {error && (
            <p style={{ fontSize: 12, color: 'var(--red)', margin: '0 0 14px' }} role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn b-dk" disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', padding: '10px 16px', fontWeight: 600 }}
            data-testid="register-submit">
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '18px 0 0', textAlign: 'center' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--ink3)', textDecoration: 'none', fontWeight: 600 }}>
            Sign in →
          </Link>
        </p>
      </div>
    </div>
  )
}
